import React, { useEffect } from 'react'
import CloseIcon from '@material-ui/icons/Close'
import { Button, Dialog, DialogActions, DialogContent, IconButton, makeStyles, Paper, Slide } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import AnnouncementContent from 'shared/components/engagement/AnnouncementContent'
import type { Announcement } from './announcementQueue'

const useStyles = makeStyles((theme: Theme) => ({
  dialogContent: {
    position: 'relative',
    paddingTop: theme.spacing(3)
  },
  close: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 1
  },
  popup: {
    position: 'fixed',
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    width: 360,
    maxWidth: `calc(100vw - ${theme.spacing(4)}px)`,
    maxHeight: `calc(100vh - ${theme.spacing(6)}px)`,
    overflowY: 'auto',
    padding: theme.spacing(2.5, 2.5, 2),
    zIndex: theme.zIndex.snackbar,
    [theme.breakpoints.down('xs')]: {
      right: theme.spacing(2),
      bottom: theme.spacing(2)
    }
  },
  popupActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    gap: theme.spacing(1)
  }
}))

export interface AnnouncementViewProps {
  announcement: Announcement
  /** `popup` is a card in the corner that does not block the page; `modal` is a dialog. */
  mode: 'modal' | 'popup'
  onClose: () => void
  onCta: () => void
}

/** One open announcement, as a modal dialog or a corner popup (#25346). */
export default function AnnouncementView({ announcement, mode, onClose, onCta }: AnnouncementViewProps) {
  const classes = useStyles()
  const { title, body, image, publishedAt, cta } = announcement
  const titleId = `announcement-title-${announcement.id}`

  // The modal Dialog closes on Escape by itself; the popup is not a Dialog, so do it here.
  useEffect(() => {
    if (mode !== 'popup') return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [mode, onClose])

  const closeButton = (
    <IconButton className={classes.close} size="small" onClick={onClose} aria-label="Close announcement">
      <CloseIcon fontSize="small" />
    </IconButton>
  )
  const ctaButton = cta ? (
    <Button variant="contained" color="primary" onClick={onCta}>
      {cta.label}
    </Button>
  ) : null

  if (mode === 'popup') {
    return (
      <Slide direction="up" in mountOnEnter>
        <Paper className={classes.popup} elevation={8} role="dialog" aria-labelledby={titleId}>
          {closeButton}
          <AnnouncementContent
            title={title}
            body={body}
            image={image}
            publishedAt={publishedAt}
            titleId={titleId}
            dense
          />
          {ctaButton && <div className={classes.popupActions}>{ctaButton}</div>}
        </Paper>
      </Slide>
    )
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-labelledby={titleId}>
      <DialogContent className={classes.dialogContent}>
        {closeButton}
        <AnnouncementContent title={title} body={body} image={image} publishedAt={publishedAt} titleId={titleId} />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Close
        </Button>
        {ctaButton}
      </DialogActions>
    </Dialog>
  )
}
